<template>
  <div class="form-template-container" ref="editor">
    <div class="editor-layout">

      <!-- RIGHT SIDEBAR: Page Thumbnails (RTL = right side) -->
      <aside class="thumbnails-sidebar" ref="thumbnailsSidebar">
        <h3 class="thumbnails-title">עמודים</h3>
        <div class="thumbnails-list">
          <div v-if="images.length < 1" class="thumbnail-item">
            <div class="thumbnail-placeholder loading"></div>
            <span class="page-badge">1</span>
          </div>
          <div
            v-for="(image, index) in images"
            :key="index"
            class="thumbnail-item"
            :ref="`thumb-${index}`"
            @click="scrollToPage(index)"
          >
            <div class="thumbnail-placeholder" :class="{ 'active': currentPage === index }">
              <img :src="`data:image/jpg;base64, ${image.data}`" class="thumbnail-img" alt="" />
            </div>
            <span class="page-badge" :class="{ 'active': currentPage === index }">{{ index + 1 }}</span>
          </div>
        </div>
      </aside>

      <!-- CENTER: Document Area -->
      <main class="document-area" ref="documentArea">
        <div v-if="images.length < 1" class="loading-overlay">
          <div class="spinner">
            <div class="double-bounce1"></div>
            <div class="double-bounce2"></div>
          </div>
        </div>
        <div class="document-scroll" v-if="!getInProgress" @click.self="currField = null">
          <div class="all-imgs-container" @click.self="currField = null">
            <DraggableContainer class="all-imgs-items" @click.self="currField = null">
              <!-- eslint-disable vue/no-use-v-if-with-v-for,vue/no-confusing-v-for-v-if -->
              <div class="all-imgs-item" v-for="(image, index) in images" :key="index" :ref="`imgNumber${index + 1}`" style="position: relative;" @click.self="currField = null">
                <img :src="`data:image/jpg;base64, ${image.data}`" alt="image" @click="currField = null" />
                <div
                  v-if="getPastSignedAttributes(index + 1)"
                  v-for="(field, idx) in getPastSignedAttributes(index + 1)"
                  :key="idx"
                  :style="{
                    position: 'absolute',
                    left: field.x + 'px',
                    top: field.y + 'px',
                    width: (field.type === 'checkbox' ? 28 : (field.width || 150)) + 'px',
                    height: (field.type === 'checkbox' ? 28 : (field.height || 100)) + 'px',
                    zIndex: 2
                  }"
                  :class="[
                    'past-attribute',
                    field.type === 'signature' ? 'past-signature-image' : '',
                    field.type === 'signatureDate' || field.type === 'assigneeName' || field.type === 'label' || field.type === 'textbox' ? 'past-text-field' : '',
                    field.type === 'checkbox' ? 'past-checkbox-field' : ''
                  ]"
                >
                  <div v-if="field.type === 'signature'" :style="{ width: '100%', height: '100%', backgroundImage: `url(${field.value})`, backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundSize: 'contain', imageRendering: 'crisp-edges' }"></div>
                  <div v-else-if="field.type === 'signatureDate' || field.type === 'assigneeName' || field.type === 'label' || field.type === 'textbox'" :style="{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#000', textAlign: 'center', padding: '2px 4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }">{{ field.value }}</div>
                  <div v-else-if="field.type === 'checkbox'" :style="{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }">
                    <input type="checkbox" :checked="field.value === true || field.value === 'true'" disabled :style="{ width: '15px', height: '15px', cursor: 'default', pointerEvents: 'none' }" />
                  </div>
                </div>
              </div>

              <!-- Signature fields -->
              <Vue3DraggableResizable
                v-if="getSignatureFields() && images.length && showAttributes"
                :class="getBackgroundColor(field.assigneeNumber)"
                v-for="(field) in getSignatureFields()"
                :key="`sig-${field.id}`"
                :initW="field.width || 130" :initH="field.height || 85"
                :x="field.x" :y="getAbsoluteY(field)"
                :w="field.width" :h="field.height"
                :active="active" :draggable="true" :resizable="true" :parent="true"
                :lockAspectRatio="true"
                @resize-end="handleResizable(field.id, $event)"
                @drag-end="handleDraggable(field.id, $event)">
                <div class="draggable-resizable-field signature-field">
                  <span>חתימה - {{ getFieldAssigneeLabel(field) }}</span>
                  <button class="remove-button" @click="removeField(field.id)">x</button>
                </div>
              </Vue3DraggableResizable>

              <!-- Date fields -->
              <Vue3DraggableResizable
                v-if="getDateFields() && images.length && showAttributes"
                :class="[getBackgroundColor(field.assigneeNumber), 'text-field-container']"
                v-for="(field) in getDateFields()"
                :key="`date-${field.id}`"
                :initW="field.width || 130" :initH="25"
                :x="field.x" :y="getAbsoluteY(field)"
                :w="field.width" :h="25"
                :active="active" :draggable="true" :resizable="true" :parent="true"
                :lockAspectRatio="false"
                @resize-end="handleResizable(field.id, $event)"
                @drag-end="handleDraggable(field.id, $event)">
                <div class="draggable-resizable-field text-field">
                  <span>תאריך חתימה - {{ getFieldAssigneeLabel(field) }}</span>
                  <button class="remove-button" @click="removeField(field.id)">x</button>
                </div>
              </Vue3DraggableResizable>

              <!-- Assignee name fields -->
              <Vue3DraggableResizable
                v-if="getAssigneeNameFields() && images.length && showAttributes"
                :class="[getBackgroundColor(field.assigneeNumber), 'text-field-container']"
                v-for="(field) in getAssigneeNameFields()"
                :key="`assigneeName-${field.id}`"
                :initW="field.width || 130" :initH="25"
                :x="field.x" :y="getAbsoluteY(field)"
                :w="field.width" :h="25"
                :active="active" :draggable="true" :resizable="true" :parent="true"
                :lockAspectRatio="false"
                @resize-end="handleResizable(field.id, $event)"
                @drag-end="handleDraggable(field.id, $event)">
                <div class="draggable-resizable-field text-field">
                  <span>שם החותם - {{ getFieldAssigneeLabel(field) }}</span>
                  <button class="remove-button" @click="removeField(field.id)">x</button>
                </div>
              </Vue3DraggableResizable>

              <!-- Label, textbox, checkbox fields -->
              <Vue3DraggableResizable
                v-if="getLabelTextboxAndCheckboxAttributeFields() && images.length && showAttributes"
                :class="[getBackgroundColor(field.assigneeNumber, field.type), field.type === 'checkbox' ? 'checkbox-field-container' : 'text-field-container']"
                :data-type="field.type"
                v-for="(field) in getLabelTextboxAndCheckboxAttributeFields()"
                :key="`${field.type}-${field.id}`"
                :initW="field.type === 'checkbox' ? 28 : (field.width || 130)"
                :initH="field.type === 'checkbox' ? 28 : 25"
                :x="field.x"
                :y="getAbsoluteY(field)"
                :w="field.type === 'checkbox' ? 28 : field.width"
                :h="field.type === 'checkbox' ? 28 : 25"
                :active="active"
                :draggable="true"
                :resizable="field.type !== 'checkbox'"
                :parent="true"
                :lockAspectRatio="field.type === 'checkbox'"
                @resize-end="handleResizable(field.id, $event)"
                @drag-end="handleDraggable(field.id, $event)">
                <div :class="['draggable-resizable-field', field.type === 'checkbox' ? 'checkbox-field' : 'text-field']">
                  <span v-if="field.type === 'label'">{{ field.displayName || 'תווית' }}</span>
                  <span v-else-if="field.type === 'textbox'">{{ getTextboxDisplayText(field) }}</span>
                  <span v-else-if="field.type === 'checkbox'">
                    <input type="checkbox" :checked="field.isChecked" class="field-checkbox" readonly disabled />
                  </span>
                  <button class="remove-button" @click="removeField(field.id)">x</button>
                </div>
              </Vue3DraggableResizable>

            </DraggableContainer>
          </div>
        </div>
      </main>

      <!-- LEFT SIDEBAR: Fields Panel (RTL = left side) -->
      <aside class="fields-sidebar">
        <!-- Header -->
        <div class="sidebar-header">
          <h3 class="sidebar-title">שדות לחתימה</h3>
          <p class="sidebar-subtitle">לחץ על שדה כדי להוסיף אותו למסמך</p>
        </div>

        <!-- Scrollable fields list -->
        <div class="fields-scroll">
          <!-- Dynamic assignee sections -->
          <div v-for="(option, idx) in fieldOptions" :key="`section-${idx}`" class="field-section">
            <div class="section-header" @click="toggleSection(`assignee-${idx}`)">
              <div class="section-dot" :class="`dot-${getAssigneeColorClass(option.Buttons[0].assigneeNumber)}`"></div>
              <h4 class="section-title-text">{{ option.Title }}</h4>
              <font-awesome-icon icon="fa-solid fa-chevron-down" class="section-chevron" :class="{ 'collapsed': isCollapsed(`assignee-${idx}`) }" />
            </div>
            <div class="field-cards" v-show="!isCollapsed(`assignee-${idx}`)">
              <div
                v-for="(button, index) in option.Buttons"
                :key="`btn-${idx}-${index}`"
                class="field-card"
                :data-assignee="button.assigneeNumber"
                @click="AddField(idx, index)"
              >
                <font-awesome-icon :icon="getFieldIcon(button.Value)" class="field-icon" :class="`icon-${getAssigneeColorClass(button.assigneeNumber)}`" />
                <span class="field-card-name">{{ button.Text }}</span>
              </div>
            </div>
          </div>

          <!-- Representer section (assignee 99) -->
          <template v-if="isSpecialAttributesAllowed && queryAssigneeNumber !== 99">
            <div class="field-section">
              <div class="section-header" @click="toggleSection('representer')">
                <div class="section-dot dot-representer"></div>
                <h4 class="section-title-text">חתימת מייצג</h4>
                <font-awesome-icon icon="fa-solid fa-chevron-down" class="section-chevron" :class="{ 'collapsed': isCollapsed('representer') }" />
              </div>
              <div class="field-cards" v-show="!isCollapsed('representer')">
                <div
                  v-for="(button, index) in [{ Value: 'signature', Text: 'חתימה', assigneeNumber: 99 }, { Value: 'signatureDate', Text: 'תאריך חתימה', assigneeNumber: 99 }, { Value: 'assigneeName', Text: 'שם החותם/ת', assigneeNumber: 99 }]"
                  :key="`rep-${index}`"
                  class="field-card"
                  data-assignee="99"
                  @click="AddField(0, index, button)"
                >
                  <font-awesome-icon :icon="getFieldIcon(button.Value)" class="field-icon icon-representer" />
                  <span class="field-card-name">{{ button.Text }}</span>
                </div>
              </div>
            </div>
          </template>

          <!-- General fields section -->
          <div class="field-section">
            <div class="section-header" @click="toggleSection('general')">
              <div class="section-dot dot-general"></div>
              <h4 class="section-title-text">שדות כלליים</h4>
              <font-awesome-icon icon="fa-solid fa-chevron-down" class="section-chevron" :class="{ 'collapsed': isCollapsed('general') }" />
            </div>
            <div class="field-cards" v-show="!isCollapsed('general')">
              <div class="field-card" data-assignee="label" @click="AddClientDetailsField">
                <font-awesome-icon :icon="getFieldIcon('label')" class="field-icon icon-general" />
                <span class="field-card-name">תווית</span>
              </div>
              <div class="field-card" data-assignee="textbox" @click="AddtextboxField">
                <font-awesome-icon :icon="getFieldIcon('textbox')" class="field-icon icon-general" />
                <span class="field-card-name">שדה טקסט</span>
              </div>
              <div class="field-card" data-assignee="checkbox" @click="AddcheckboxField">
                <font-awesome-icon :icon="getFieldIcon('checkbox')" class="field-icon icon-general" />
                <span class="field-card-name">תיבת סימון</span>
              </div>
            </div>
          </div>
        </div><!-- end fields-scroll -->

        <!-- Footer: Properties + Save Button -->
        <div class="sidebar-footer">

          <!-- Field Properties -->
          <div v-if="currField" class="field-properties">
            <div class="properties-section-header" @click="propertiesCollapsed = !propertiesCollapsed">
              <font-awesome-icon icon="fa-solid fa-gear" class="props-header-icon" />
              <span>מאפייני השדה</span>
              <font-awesome-icon
                :icon="propertiesCollapsed ? 'fa-solid fa-chevron-up' : 'fa-solid fa-minus'"
                class="props-toggle-icon"
              />
            </div>
            <table class="properties-table" v-show="!propertiesCollapsed">
              <tbody>
                <!-- Field Type -->
                <tr>
                  <td class="prop-label">סוג שדה</td>
                  <td class="prop-value">{{ (currField.type === 'label' || currField.type === 'textbox' || currField.type === 'checkbox') ? getFieldTypeDisplayName(currField.type) : (currField.assigneeNumber === 99 ? 'חתימת מייצג' : currField.assigneeNumber === 2 ? 'חותם 2' : 'חותם 1') + ' - ' + getFieldTypeDisplayName(currField.type) }}</td>
                </tr>
                <!-- Linked Data (label) -->
                <tr v-if="currField.type === 'label'">
                  <td class="prop-label">נתון מקושר</td>
                  <td class="prop-value prop-dropdown-cell">
                    <div v-if="!selectOptions || selectOptions.length === 0" class="no-options-message">טוען...</div>
                    <BDropdown v-else v-model="currField.bindingId" @change="onBindingChange" class="buefy-dropdown" expanded aria-role="list" :mobile-modal="false" :overlay="false" v-model:active="isBindingDropdownOpen">
                      <template #trigger>
                        <button class="dropdown-trigger binding-trigger" :class="{ 'active': isBindingDropdownOpen }" @click="isBindingDropdownOpen = !isBindingDropdownOpen">
                          <span>{{ getBindingDisplayName(currField.bindingId) || 'בחר נתון' }}</span>
                          <span class="dropdown-arrow">▼</span>
                        </button>
                      </template>
                      <div class="search-container">
                        <BInput v-model="bindingSearchQuery" placeholder="חיפוש..." icon="magnify" size="is-small" class="search-input" />
                        <div v-if="isLoadingDropdowns" class="loading-indicator"><span>טוען...</span></div>
                      </div>
                      <div class="dropdown-content">
                        <template v-if="filteredBindingOptions && filteredBindingOptions.length > 0">
                          <template v-for="group in filteredBindingOptions" :key="group.label">
                            <div class="group-header"><strong>{{ group.label }}</strong></div>
                            <div v-for="option in group.options" :key="option.value" class="group-option" @click.stop="onBindingSelect(option)">{{ option.label }}</div>
                            <div v-if="group !== filteredBindingOptions[filteredBindingOptions.length - 1]" class="group-separator"></div>
                          </template>
                        </template>
                        <template v-else>
                          <div class="no-options-message">אין אפשרויות זמינות</div>
                        </template>
                      </div>
                    </BDropdown>
                  </td>
                </tr>
                <!-- Linked Data (textbox) -->
                <tr v-if="currField.type === 'textbox'">
                  <td class="prop-label">נתון מקושר</td>
                  <td class="prop-value prop-dropdown-cell">
                    <div v-if="!selectOptions || selectOptions.length === 0" class="no-options-message">טוען...</div>
                    <BDropdown v-else v-model="currField.bindingId" @change="onBindingChange" class="buefy-dropdown" expanded aria-role="list" :mobile-modal="false" :overlay="false" v-model:active="isBindingDropdownOpen">
                      <template #trigger>
                        <button class="dropdown-trigger binding-trigger" :class="{ 'active': isBindingDropdownOpen }" @click="isBindingDropdownOpen = !isBindingDropdownOpen">
                          <span>{{ getBindingDisplayName(currField.bindingId) || 'בחר נתון' }}</span>
                          <span class="dropdown-arrow">▼</span>
                        </button>
                      </template>
                      <div class="search-container">
                        <BInput v-model="bindingSearchQuery" placeholder="חיפוש..." icon="magnify" size="is-small" class="search-input" />
                        <div v-if="isLoadingDropdowns" class="loading-indicator"><span>טוען...</span></div>
                      </div>
                      <div class="dropdown-content">
                        <div class="group-option" @click.stop="onBindingSelect({ value: null, label: 'ללא נתון מקושר' })">ללא נתון מקושר</div>
                        <div class="group-separator"></div>
                        <template v-if="filteredBindingOptions && filteredBindingOptions.length > 0">
                          <template v-for="group in filteredBindingOptions" :key="group.label">
                            <div class="group-header"><strong>{{ group.label }}</strong></div>
                            <div v-for="option in group.options" :key="option.value" class="group-option" @click.stop="onBindingSelect(option)">{{ option.label }}</div>
                            <div v-if="group !== filteredBindingOptions[filteredBindingOptions.length - 1]" class="group-separator"></div>
                          </template>
                        </template>
                        <template v-else>
                          <div class="no-options-message">אין אפשרויות זמינות</div>
                        </template>
                      </div>
                    </BDropdown>
                  </td>
                </tr>
                <!-- Text (textbox) -->
                <tr v-if="currField.type === 'textbox'">
                  <td class="prop-label">טקסט</td>
                  <td class="prop-value">
                    <input type="text" v-model="currField.displayText" placeholder="מלל ברירת מחדל..." class="property-input textbox-text-input" />
                  </td>
                </tr>
                <!-- Default (checkbox) -->
                <tr v-if="currField.type === 'checkbox'">
                  <td class="prop-label">ברירת מחדל</td>
                  <td class="prop-value">
                    <input type="checkbox" v-model="currField.isChecked" class="property-checkbox" />
                  </td>
                </tr>
                <!-- Height -->
                <tr>
                  <td class="prop-label">גובה</td>
                  <td class="prop-value"><input id="height" @focus="$event.target.select()" type="text" readonly v-model="currField.height" class="property-input" /></td>
                </tr>
                <!-- Width -->
                <tr>
                  <td class="prop-label">רוחב</td>
                  <td class="prop-value"><input id="width" @focus="$event.target.select()" type="text" readonly v-model="currField.width" class="property-input" /></td>
                </tr>
                <!-- Y -->
                <tr>
                  <td class="prop-label">מלמעלה</td>
                  <td class="prop-value"><input id="y" @focus="$event.target.select()" type="text" readonly v-model="currField.y" class="property-input" /></td>
                </tr>
                <!-- X -->
                <tr>
                  <td class="prop-label">משמאל</td>
                  <td class="prop-value"><input id="x" @focus="$event.target.select()" type="text" readonly v-model="currField.x" class="property-input" /></td>
                </tr>
                <!-- Page -->
                <tr>
                  <td class="prop-label">דף</td>
                  <td class="prop-value"><input id="page" @focus="$event.target.select()" type="text" readonly v-model="currField.page" class="property-input" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Save Button -->
          <div class="footer-actions">
            <button
              class="save-btn"
              @click="saveTemplate"
              :disabled="fields.length < 1 || inProgress"
              :class="{ 'disabled': fields.length < 1 || inProgress }"
            >
              <span v-if="!inProgress">
                <span v-if="isUpdate">עדכן</span>
                <span v-else>שמור והמשך</span>
                <font-awesome-icon icon="fa-solid fa-floppy-disk" style="margin-right: 6px;" />
              </span>
              <span v-else>שומר...</span>
            </button>
          </div>

        </div><!-- end sidebar-footer -->
      </aside><!-- end fields-sidebar -->

    </div><!-- end editor-layout -->

    <!-- Toasts (teleported to body to escape overflow:hidden) -->
    <Teleport to="body">
      <transition name="toast-fade">
        <div v-if="toasts.instruction" class="toast-pill">
          <font-awesome-icon icon="fa-solid fa-circle-info" class="toast-icon" />
          <p class="toast-message">לחץ על שדה בצד הימני כדי להוסיף אותו למסמך</p>
          <button class="toast-close" @click="dismissToast('instruction')">
            <font-awesome-icon icon="fa-solid fa-xmark" />
          </button>
        </div>
      </transition>
      <transition name="toast-fade">
        <div v-if="toasts.success" class="toast-pill toast-success">
          <font-awesome-icon icon="fa-solid fa-circle-check" class="toast-icon toast-icon-success" />
          <p class="toast-message">שדות החתימה מוקמו בהצלחה!</p>
          <button class="toast-close" @click="dismissToast('success')">
            <font-awesome-icon icon="fa-solid fa-xmark" />
          </button>
        </div>
      </transition>
      <transition name="toast-fade">
        <div v-if="toasts.error" class="toast-pill toast-error">
          <font-awesome-icon icon="fa-solid fa-circle-exclamation" class="toast-icon toast-icon-error" />
          <p class="toast-message">אירעה שגיאה בשמירה. נסה שנית.</p>
          <button class="toast-close" @click="dismissToast('error')">
            <font-awesome-icon icon="fa-solid fa-xmark" />
          </button>
        </div>
      </transition>
    </Teleport>

  </div>
</template>

<script>
import DocumentService from "@/services/DocumentService.js";
import Vue3DraggableResizable from "vue3-draggable-resizable";
import { BDropdown, BInput } from "buefy";
import axios from "axios";


export default {
  name: "FormTemplate",
  data() {
    return {
      active: false,
      fieldOptions: [],
      fields: [],
      images: [],
      currField: null,
      showAttributes: false,
      pastSignedAttributes: [],
      activeAttr: null,
      activeElement: null,
      isSpecialAttributesAllowed: false,
      isSelectOpen: false,
      isUpdate: false,
      docIdInTemplate: null,
      queryAssigneeNumber: null,
      inProgress: false,
      isLoadingDropdowns: false,
      isDropdown1Open: false,
      isDropdown2Open: false,
      selectedOption1: null,
      selectedOption2: null,
      searchQuery1: '',
      searchQuery2: '',
      bindingSearchQuery: '',
      isBindingDropdownOpen: false,
      selectOptions: [],
      toasts: {
        instruction: false,
        success: false,
        error: false,
      },
      toastTimers: {},
      collapsedSections: {},
      currentPage: 0,
      propertiesCollapsed: false,
    };
  },
  components: {
    Vue3DraggableResizable,
    BDropdown,
    BInput,
  },
  created() {
    this.isUpdate = this.$route.name === "FormUpdateTemplate";
    this.queryAssigneeNumber = +this.$route.query.assigneeNumber;

    switch (this.queryAssigneeNumber) {
      case 1:
        this.fieldOptions = [{
          Title: 'חותם 1',
          Buttons:
            [{ Value: "signature", Text: "חתימה", assigneeNumber: 1 }, { Value: 'signatureDate', Text: 'תאריך חתימה', assigneeNumber: 1 }, { Value: 'assigneeName', Text: 'שם החותם/ת', assigneeNumber: 1 }]
        }];
        break;
      case 2:
        this.fieldOptions = [{
          Title: 'חותם 2',
          Buttons:
            [{ Value: "signature", Text: "חתימה", assigneeNumber: 2 }, { Value: 'signatureDate', Text: 'תאריך חתימה', assigneeNumber: 2 }, { Value: 'assigneeName', Text: 'שם החותם/ת', assigneeNumber: 2 }]
        }];
        break;
      case 99:
        this.fieldOptions = [{
          Title: 'חתימת מייצג',
          Buttons:
            [{ Value: "signature", Text: "חתימה", assigneeNumber: 99 }, { Value: 'signatureDate', Text: 'תאריך חתימה', assigneeNumber: 99 }, { Value: 'assigneeName', Text: 'שם החותם/ת', assigneeNumber: 99 }]
        }];
        break;

      default:
        this.fieldOptions =
          [{
            Title: 'חותם 1',
            Buttons:
              [{ Value: "signature", Text: "חתימה", assigneeNumber: 1 }, { Value: 'signatureDate', Text: 'תאריך חתימה', assigneeNumber: 1 }, { Value: 'assigneeName', Text: 'שם החותם/ת', assigneeNumber: 1 }]
          },
          {
            Title: 'חותם 2',
            Buttons:
              [{ Value: "signature", Text: "חתימה", assigneeNumber: 2 }, { Value: 'signatureDate', Text: 'תאריך חתימה', assigneeNumber: 2 }, { Value: 'assigneeName', Text: 'שם החותם/ת', assigneeNumber: 2 }]
                   }]
         break;
     }
   },
  beforeCreate() {
    axios.defaults.headers.common["x-access-token"] =
      this.$route.params.accessToken;
    this.isUpdate = this.$route.name === "FormUpdateTemplate";
    if (!this.isUpdate) {
      DocumentService.getDocument(this.$route.params.id, this.$route.query.assigneeToken).then((res) => {
        const doc = res.data;

        this.docIdInTemplate = doc.id || this.$route.params.id;

        this.images = doc.images;
        this.attributes = doc.attributes;
        this.pastSignedAttributes = doc.pastSignedAttributes;
        this.isSpecialAttributesAllowed = doc.isSpecialAttributesAllowed;

        this.fetchDropdownOptions();

        setTimeout(() => {
          this.showAttributes = true;
        })
        this.showToast('instruction');
      });
    } else {
      DocumentService.getTemplate(this.$route.params.id).then((res) => {
        const tem = res.data;
        const doc = tem.document;
        this.docIdInTemplate = doc.id;
        this.images = doc.images;
        this.attributes = doc.attributes;
        this.isSpecialAttributesAllowed = tem.isSpecialAttributesAllowed;

        this.fetchDropdownOptions();

        tem.assignees[0].attributes.forEach((field, index) => {
          if (field.page > 1 && doc.images[index] && doc.images[index].size) {
            // field.y = field.y + (field.page - 1) * doc.images[index].size.height;
          }
          // delete field.page;
        });
        this.fields = tem.assignees[0].attributes;
        if (tem.assignees[1] && tem.assignees[1].attributes) {
          tem.assignees[1].attributes.forEach((a) => this.fields.push(a));
        }
        if (tem.assignees[2] && tem.assignees[2].attributes) {
          tem.assignees[2].attributes.forEach((a) => this.fields.push(a));
        }
        this.fields.forEach((f) => f.id = this.getAttrRandomId());

        this.$nextTick(() => {
          this.populateExistingLabelDisplayNames();
          this.ensureCheckboxDimensions();
        });

        setTimeout(() => {
          this.showAttributes = true;
        })
      });
    }
  },
  mounted() {
    this.$nextTick(() => {
      const docArea = this.$refs.documentArea;
      if (docArea) {
        docArea.addEventListener('scroll', this.onDocumentScroll, { passive: true });
      }
    });
  },
  beforeUnmount() {
    const docArea = this.$refs.documentArea;
    if (docArea) {
      docArea.removeEventListener('scroll', this.onDocumentScroll);
    }
  },
  computed: {
    isDocumentValid() {
      return (
        this.attributes.filter((a) => a.isValid).length ===
        this.attributes.length
      );
    },
    isSignatureField() {
      return this.currField && this.currField.type === 'signature';
    },
    isTextField() {
      return this.currField && (this.currField.type === 'signatureDate' || this.currField.type === 'assigneeName' || this.currField.type === 'label' || this.currField.type === 'textbox' || this.currField.type === 'checkbox');
    },
    fieldTypeLabel() {
       if (!this.currField) return '';
       switch (this.currField.type) {
         case 'signature':
           return 'שדה חתימה';
         case 'signatureDate':
           return 'שדה תאריך';
         case 'label':
           return 'שדה תווית';
         case 'textbox':
           return 'שדה טקסט';
         case 'checkbox':
           return 'תיבת סימון';
         default:
           return 'שדה לא ידוע';
       }
     },
     filteredOptions1() {
       if (!this.searchQuery1) return this.selectOptions;
       const query = this.searchQuery1.toLowerCase();
       return this.selectOptions.map(group => ({
         ...group,
         options: group.options.filter(option =>
           option.label.toLowerCase().includes(query) ||
           group.label.toLowerCase().includes(query)
         )
       })).filter(group => group.options.length > 0);
     },
     filteredOptions2() {
       if (!this.searchQuery2) return this.selectOptions;
       const query = this.searchQuery2.toLowerCase();
       return this.selectOptions.map(group => ({
         ...group,
         options: group.options.filter(option =>
           option.label.toLowerCase().includes(query) ||
           group.label.toLowerCase().includes(query)
         )
       })).filter(group => group.options.length > 0);
     },
     filteredBindingOptions() {
       if (!this.bindingSearchQuery) return this.selectOptions;
       const query = this.bindingSearchQuery.toLowerCase();
       return this.selectOptions.map(group => ({
         ...group,
         options: group.options.filter(option =>
           option.label.toLowerCase().includes(query) ||
           group.label.toLowerCase().includes(query)
         )
       })).filter(group => group.options.length > 0);
     },
     getInProgress() {
      return this.images.length < 1;
     }
  },
  watch: {
    'currField.width': function(newWidth) {
      if (this.currField && (this.currField.type === 'signatureDate' || this.currField.type === 'assigneeNumber' || this.currField.type === 'label' || this.currField.type === 'textbox' || this.currField.type === 'checkbox')) {
        if (this.currField.type === 'checkbox') {
          this.currField.width = 28;
        } else if (newWidth < 80) {
          this.currField.width = 80;
        } else if (newWidth > 350) {
          this.currField.width = 350;
        }
      }
    },
    'currField.height': function() {
      if (this.currField && this.currField.type === 'checkbox') {
        this.currField.height = 28;
      }
    },
    'currField': function(newField) {
      if (newField) {
        this.$nextTick(() => {
          this.debugDropdownState();
        });
      }
    },
    'bindingSearchQuery': function(newQuery) {
      console.log('Search query changed:', newQuery);
      this.debugSearch();
    },
    'isBindingDropdownOpen': function(newValue) {
      if (!newValue) {
        this.bindingSearchQuery = '';
      }
    }
  },
  methods: {

    toggleSection(key) {
      this.collapsedSections[key] = !this.collapsedSections[key];
    },

    isCollapsed(key) {
      return !!this.collapsedSections[key];
    },

    scrollToPage(index) {
      const docArea = this.$refs.documentArea;
      if (!docArea) return;
      const pageEls = docArea.querySelectorAll('.all-imgs-item');
      if (!pageEls[index]) return;
      const pageTop = pageEls[index].getBoundingClientRect().top;
      const areaTop = docArea.getBoundingClientRect().top;
      docArea.scrollTo({
        top: docArea.scrollTop + (pageTop - areaTop) - 32,
        behavior: 'smooth',
      });
      this.currentPage = index;
    },

    onDocumentScroll() {
      const docArea = this.$refs.documentArea;
      if (!docArea) return;
      const pageEls = docArea.querySelectorAll('.all-imgs-item');
      if (!pageEls.length) return;
      const areaRect = docArea.getBoundingClientRect();
      const areaHeight = areaRect.height;
      // Find page with most viewport coverage; only switch if it covers ≥70% of viewport
      let bestPage = -1;
      let bestVisible = 0;
      for (let i = 0; i < pageEls.length; i++) {
        const pageRect = pageEls[i].getBoundingClientRect();
        const visibleTop = Math.max(pageRect.top, areaRect.top);
        const visibleBottom = Math.min(pageRect.bottom, areaRect.bottom);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);
        if (visibleHeight > bestVisible) {
          bestVisible = visibleHeight;
          bestPage = i;
        }
      }
      if (bestPage >= 0 && bestVisible >= areaHeight * 0.7 && bestPage !== this.currentPage) {
        this.currentPage = bestPage;
        this.$nextTick(() => {
          const thumbRefs = this.$refs[`thumb-${bestPage}`];
          const thumbEl = Array.isArray(thumbRefs) ? thumbRefs[0] : thumbRefs;
          if (thumbEl) thumbEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      }
    },

    showToast(type) {
      const duration = type === 'instruction' ? 10000 : 4000;
      this.toasts[type] = true;
      clearTimeout(this.toastTimers[type]);
      this.toastTimers[type] = setTimeout(() => {
        this.toasts[type] = false;
      }, duration);
    },

    dismissToast(type) {
      this.toasts[type] = false;
      clearTimeout(this.toastTimers[type]);
    },

    getAssigneeColorClass(assigneeNumber) {
      if (assigneeNumber === 1) return 'assignee1';
      if (assigneeNumber === 2) return 'assignee2';
      if (assigneeNumber === 99) return 'representer';
      return 'general';
    },

    getFieldAssigneeLabel(field) {
      if (field.assigneeNumber === 99) return 'מייצג';
      if (field.assigneeNumber === 2) return 'חותם 2';
      return 'חותם 1';
    },

    getBackgroundColor(assigneeNumber, fieldType = null) {
      if (assigneeNumber === 1 && (fieldType === 'label' || fieldType === 'textbox' || fieldType === 'checkbox')) return 'teal';
      if (assigneeNumber === 1) return 'ochre';
      if (assigneeNumber === 2) return 'light-gray';
      if (assigneeNumber === 99) return 'light-blue';
      return 'light-blue';
    },
    getFieldIcon(fieldType) {
      switch (fieldType) {
        case 'signature':
          return 'fa-solid fa-signature';
        case 'signatureDate':
          return 'fa-solid fa-calendar-days';
        case 'assigneeName':
          return 'fa-solid fa-id-badge';
        case 'label':
          return 'fa-solid fa-font';
        case 'textbox':
          return 'fa-solid fa-t';
        case 'checkbox':
          return 'fa-solid fa-square-xmark';
        default:
          return 'fa-solid fa-question';
      }
    },
    getTitleColor(title) {
      if (title === 'חותם 1') return 'title-ochre';
      if (title === 'חותם 2') return 'title-light-gray';
      if (title === 'חתימת מייצג') return 'title-light-blue';
      return '';
    },
    getUnsetField() {
      const images = [...document.querySelectorAll('.all-imgs-item')]
      const scrollContainer = this.$refs.documentArea;
      const yOffset = scrollContainer ? scrollContainer.scrollTop : window.pageYOffset;
      let pageIdx = 0;
      let acc = 0;
      for (let i = 0; i < images.length; i++) {
        if (!yOffset) {
          pageIdx = 1;
          break;
        }
        if (acc + 0.7 * images[i].offsetHeight < yOffset) {
          acc += images[i].offsetHeight;
        } else {
          pageIdx = i + 1;
          break;
        }
      }
      return {
        x: 100,
        y: 300 + (yOffset - acc),
        width: 150,
        height: 50,
        page: pageIdx,
        id: this.getAttrRandomId()
      };
    },
    handleResizable(attrId, ev) {
      const idx = this.fields.findIndex((f) => f.id === attrId);
      if (idx === -1) return;

      if (this.fields[idx].type === 'checkbox') {
        this.fields[idx].width = 28;
        this.fields[idx].height = 28;
      } else {
        this.fields[idx].width = ev.w;
        this.fields[idx].height = ev.h;
      }

      // ev.y is absolute within the DraggableContainer (all pages stacked).
      // Convert to page-relative Y the same way handleDraggable does.
      const pageElements = document.querySelectorAll('.all-imgs-item');
      if (pageElements.length === 0) {
        this.fields[idx].x = ev.x;
        this.fields[idx].y = ev.y;
        return;
      }

      let targetPage = 1;
      let pageStartY = 0;

      for (let i = 0; i < pageElements.length; i++) {
        const cumulativeHeight = this.getCumulativePageHeight(i + 1);
        if (ev.y < cumulativeHeight) {
          targetPage = i + 1;
          pageStartY = this.getCumulativePageHeight(i);
          break;
        }
        if (i === pageElements.length - 1) {
          targetPage = pageElements.length;
          pageStartY = this.getCumulativePageHeight(i);
        }
      }

      targetPage = Math.max(1, Math.min(targetPage, this.images.length));

      this.fields[idx].x = ev.x;
      this.fields[idx].y = ev.y - pageStartY;
      this.fields[idx].page = targetPage;
      this.fields[idx].newCurrentPage = targetPage;
      this.currField = this.fields[idx];
    },
    handleDraggable(attrId, ev) {
      let idx = this.fields.findIndex((f) => f.id === attrId);
      if (idx === -1) return;

      const pageElements = document.querySelectorAll('.all-imgs-item');
      if (pageElements.length === 0) return;

      let targetPage = 1;
      let pageStartY = 0;

      for (let i = 0; i < pageElements.length; i++) {
        const cumulativeHeight = this.getCumulativePageHeight(i + 1);

        if (ev.y < cumulativeHeight) {
          targetPage = i + 1;
          pageStartY = this.getCumulativePageHeight(i);
          break;
        }

        if (i === pageElements.length - 1) {
          targetPage = pageElements.length;
          pageStartY = this.getCumulativePageHeight(i);
        }
      }

      targetPage = Math.max(1, Math.min(targetPage, this.images.length));

      let relativeY = ev.y - pageStartY;

      this.fields[idx].x = ev.x;
      this.fields[idx].y = relativeY;
      this.fields[idx].page = targetPage;
      this.fields[idx].newCurrentPage = targetPage;
      this.currField = this.fields[idx];
    },
    AddField(idx, index, button = null) {
      let currField;

      if (button) {
        currField = {
          ...this.getUnsetField(),
          type: button.Value,
          assigneeNumber: button.assigneeNumber
        };
      } else {
        currField = {
          ...this.getUnsetField(),
          type: this.fieldOptions[idx].Buttons[index].Value,
          assigneeNumber: this.fieldOptions[idx].Buttons[index].assigneeNumber
        };
      }

      if (currField.type === 'signature') {
        currField.width = 130;
        currField.height = 85;
      } else if (currField.type === 'signatureDate') {
        currField.width = 170;
        currField.height = 25;
      } else if (currField.type === 'assigneeName') {
        currField.width = 170;
        currField.height = 25;
      } else if (currField.type === 'label' || currField.type === 'textbox') {
        currField.width = 170;
        currField.height = 25;
      } else if (currField.type === 'checkbox') {
        currField.width = 28;
        currField.height = 28;
        currField.isChecked = false;
        currField.bindingId = null;
      }
      this.fields.push(currField);

      this.currField = currField;

      this.isSelectOpen = false;
    },

    AddClientDetailsField() {
      let currField = {
        ...this.getUnsetField(),
        type: 'label',
        assigneeNumber: 1
      };
      currField.width = 170;
      currField.height = 25;

      this.fields.push(currField);
      this.currField = currField;
    },

    AddtextboxField() {
      let currField = {
        ...this.getUnsetField(),
        type: 'textbox',
        assigneeNumber: 1,
        bindingId: null
      };
      currField.width = 170;
      currField.height = 25;

      this.fields.push(currField);
      this.currField = currField;
    },

    AddcheckboxField() {
      let currField = {
        ...this.getUnsetField(),
        type: 'checkbox',
        assigneeNumber: 1,
        isChecked: false,
        bindingId: null
      };
      currField.width = 28;
      currField.height = 28;

      this.fields.push(currField);
      this.currField = currField;
    },
    removeField(id) {
      const idx = this.fields.findIndex(f => f.id === id);
      this.fields.splice(idx, 1);
      this.currField = null;
    },

    saveTemplate() {
      if (this.inProgress) return;
      this.inProgress = true;
      const arrToSend = this.fields.map((field) => {
        let heightCountBefour = 0;
        let heightCount = 0;
        let pageIdx = this.images.findIndex((_, idx) => {
          heightCountBefour = heightCount;
          heightCount += this.$refs[`imgNumber${idx + 1}`][0].clientHeight;
          return field.y < heightCount;
        });

        const y = pageIdx > 0 ? field.y - heightCountBefour : field.y;

        if (field.type === 'checkbox') {
          return {
            ...field,
            y,
            page: field.newCurrentPage || field.page,
            bindingId: field.bindingId || null,
            isChecked: field.isChecked || false
          };
        }

        if (field.type === 'textbox') {
          return {
            ...field,
            y,
            page: field.newCurrentPage || field.page,
            bindingId: field.bindingId || null,
            displayText: field.displayText || ""
          };
        }

        return { ...field, y, page: field.newCurrentPage || field.page, bindingId: field.bindingId || null };
      });

      if (!this.isUpdate) {
        const request = this.$route.query.assigneeToken ? DocumentService.updateAssigneeDocument(this.$route.params.id, this.$route.query.assigneeToken, arrToSend) : DocumentService.post(this.$route.params.id, arrToSend);
        request
          .then(() => {
            this.showToast('success');
            this.onTemplateUpdate();
          })
          .catch((err) => {
            console.log("err:", err);
            this.showToast('error');
          })
          .finally(() => {
            this.inProgress = false;
          });
      } else {
        DocumentService.updateTemplate(
          this.docIdInTemplate,
          this.$route.params.id,
          arrToSend
        )
          .then(() => {
            this.showToast('success');
            this.onTemplateUpdate();
          })
          .catch((err) => {
            console.log("err:", err);
            this.showToast('error');
          })
          .finally(() => {
            this.inProgress = false;
          });
      }
    },
    onAttributeClick(e, attr) {
      const element = e.target;
      this.activeAttr = attr;
      this.activeElement = element;
    },
    getAttributes(page = 1) {
      return this.fields.filter((a) => a.page === page && a.type === 'signature');
    },
    getDatesAttributes(page = 1) {
      return this.fields.filter((a) => a.page === page && a.type === 'signatureDate');
    },
    getAssigneeNameAttributes(page = 1) {
      return this.fields.filter((a) => a.page === page && a.type === 'assigneeName');
    },
    getLabelAttributes(page = 1) {
      return this.fields.filter((a) => a.page === page && a.type === 'label');
    },

    gettextboxAttributes(page = 1) {
      return this.fields.filter((a) => a.page === page && a.type === 'textbox');
    },

    getcheckboxAttributes(page = 1) {
      return this.fields.filter((a) => a.page === page && a.type === 'checkbox');
    },

    getPastSignedAttributes(page = 1) {
      if (!this.pastSignedAttributes) return [];
      return this.pastSignedAttributes.filter((a) => a.page === page);
    },
    getSignatureFields() {
      return this.fields.filter((field) => field.type === 'signature');
    },
    getDateFields() {
      return this.fields.filter((field) => field.type === 'signatureDate');
    },
    getAssigneeNameFields() {
      return this.fields.filter((field) => field.type === 'assigneeName');
    },

    getLabelAttributeFields() {
      return this.fields.filter((field) => field.type === 'label');
    },

    gettextboxAttributeFields() {
      return this.fields.filter((field) => field.type === 'textbox');
    },

    getcheckboxAttributeFields() {
      return this.fields.filter((field) => field.type === 'checkbox');
    },

    getLabelTextboxAndCheckboxAttributeFields() {
      return this.fields.filter((field) => field.type === 'label' || field.type === 'textbox' || field.type === 'checkbox');
    },

    getCumulativePageHeight(targetPageIndex) {
      const pageElements = document.querySelectorAll('.all-imgs-item');
      let cumulativeHeight = 0;

      for (let i = 0; i < Math.min(targetPageIndex, pageElements.length); i++) {
        cumulativeHeight += pageElements[i].offsetHeight;
      }

      return cumulativeHeight;
    },
    getAbsoluteY(field) {
      if (field.page <= 1) {
        return field.y;
      }

      const cumulativeHeight = this.getCumulativePageHeight(field.page - 1);
      return field.y + cumulativeHeight;
    },
    getAttrRandomId() {
      return Math.floor(Math.random() * 10000);
    },
    onTemplateUpdate() {
      window.parent.postMessage('ok', "*");
      window.parent.postMessage(this.$route.params.id, "*");
    },

    onSelectChange(assigneeNumber, option) {
      if (option && option.value && option.label) {
        let currField = {
          ...this.getUnsetField(),
          type: 'label',
          assigneeNumber: assigneeNumber,
          bindingId: option.value,
          displayName: option.label,
          width: 170,
          height: 25
        };

        this.fields.push(currField);
      }

      this.selectedOption1 = null;
      this.selectedOption2 = null;
      this.searchQuery1 = '';
      this.searchQuery2 = '';
    },

    async fetchDropdownOptions() {
      try {
        this.isLoadingDropdowns = true;

        const templateId = this.$route.params.id;
        if (!templateId) {
          console.error('No document ID available for fetching attribute bindings');
          this.isLoadingDropdowns = false;
          return;
        }

        const response = await DocumentService.getAttributeBindingsUserList(templateId);
        const jsonData = response.data;

        const transformedOptions = this.transformJsonToDropdownOptions(jsonData);

        this.selectOptions = transformedOptions;

        this.populateExistingLabelDisplayNames();
        this.ensureCheckboxDimensions();

      } catch (error) {
        console.error('Error fetching dropdown options:', error);
      } finally {
        this.isLoadingDropdowns = false;
      }
    },

    populateExistingLabelDisplayNames() {
      if (!this.selectOptions || !this.fields) return;

      this.fields.forEach(field => {
        if ((field.type === 'label' || field.type === 'textbox' || field.type === 'checkbox') && field.bindingId) {
          const displayName = this.getDisplayNameByBindingId(field.bindingId);
          if (displayName) {
            field.displayName = displayName;
          }
        }
      });
    },

    ensureCheckboxDimensions() {
      if (!this.fields) return;

      this.fields.forEach(field => {
        if (field.type === 'checkbox') {
          field.width = 28;
          field.height = 28;
          if (field.isChecked === undefined) {
            field.isChecked = false;
          }
          if (field.bindingId === undefined) {
            field.bindingId = null;
          }
        }
      });
    },

    getFieldTypeDisplayName(type, bindingId = null) {
      switch (type) {
        case 'signature':
          return 'חתימה';
        case 'signatureDate':
          return 'תאריך חתימה';
        case 'assigneeName':
          return 'שם החותם/ת';
        case 'label':
          if (bindingId && this.selectOptions) {
            const displayName = this.getDisplayNameByBindingId(bindingId);
            if (displayName) {
              return displayName;
            }
          }
          return 'תווית';
        case 'textbox':
          return 'שדה טקסט';
        case 'checkbox':
          return 'תיבת סימון';
        default:
          return 'שדה לא ידוע';
      }
    },

    getDisplayNameByBindingId(bindingId) {
      if (!this.selectOptions || !bindingId) return null;

      for (const group of this.selectOptions) {
        const matchingOption = group.options.find(option => option.value === bindingId);
        if (matchingOption) {
          return matchingOption.label;
        }
      }
      return null;
    },

    getBindingDisplayName(bindingId) {
      if (bindingId === null) {
        return 'ללא נתון מקושר';
      }
      return this.getDisplayNameByBindingId(bindingId);
    },

    getTextboxDisplayText(field) {
      if (field.type !== 'textbox') return '';

      if (field.displayText && field.displayText.trim() !== '') {
        return field.displayText;
      }

      if (field.bindingId) {
        const bindingDisplayName = this.getDisplayNameByBindingId(field.bindingId);
        if (bindingDisplayName) {
          return bindingDisplayName;
        }
      }

      return 'שדה טקסט';
    },

    onBindingChange() {
      // Additional logic on binding change if needed
    },

    onBindingSelect(option) {
      if (this.currField && option) {
        this.currField.bindingId = option.value;
        if (option.value === null) {
          if (this.currField.type === 'textbox') {
            this.currField.displayName = null;
          } else {
            this.currField.bindingId = null;
            this.currField.displayName = null;
          }
        } else {
          this.currField.displayName = option.label;
        }

        this.isBindingDropdownOpen = false;

        this.$nextTick(() => {
          const event = new Event('click', { bubbles: true });
          document.body.dispatchEvent(event);
        });
      }
    },

    debugDropdownState() {
      console.log('Current field:', this.currField);
      console.log('Select options:', this.selectOptions);
      console.log('Filtered binding options:', this.filteredBindingOptions);
      console.log('Binding search query:', this.bindingSearchQuery);
      console.log('Binding dropdown open:', this.isBindingDropdownOpen);
    },

    toggleBindingDropdown() {
      this.isBindingDropdownOpen = !this.isBindingDropdownOpen;
      console.log('Binding dropdown toggled:', this.isBindingDropdownOpen);
    },

    debugSearch() {
      console.log('Search query:', this.bindingSearchQuery);
      console.log('Filtered options:', this.filteredBindingOptions);
      console.log('Original options:', this.selectOptions);
    },

    debugDropdownDimensions() {
      const dropdown = document.querySelector('.buefy-dropdown');
      const content = document.querySelector('.dropdown-content');
      const trigger = document.querySelector('.dropdown-trigger');

      if (dropdown) {
        console.log('Dropdown container:', { width: dropdown.offsetWidth });
      }
      if (content) {
        console.log('Dropdown content:', { width: content.offsetWidth });
      }
      if (trigger) {
        console.log('Dropdown trigger:', { width: trigger.offsetWidth });
      }
    },

    transformJsonToDropdownOptions(jsonData) {
      if (!Array.isArray(jsonData)) {
        console.error('Invalid JSON data format - expected array');
        return [];
      }

      const groupedOptions = [];

      jsonData.forEach(section => {
        const sections = [
          'generalDetails',
          'personDetails',
          'personBankDetails',
          'spouseDetails',
          'businessDetails',
          'authoritiesDetails',
          'businessBankDetails'
        ];

        sections.forEach(sectionName => {
          if (section[sectionName] && section[sectionName].properties) {
            const sectionData = section[sectionName];
            const sectionOptions = sectionData.properties.map(prop => ({
              label: prop.displayName,
              value: prop.bindingId
            }));

            groupedOptions.push({
              label: sectionData.displayName,
              options: sectionOptions
            });
          }
        });
      });

      return groupedOptions;
    },
  },
};
</script>

<style lang="scss" scoped>

/* ─── Layout ──────────────────────────────────────────────── */

.form-template-container {
  width: 100%;
  height: 100vh;
  overflow: hidden;
  direction: rtl;
  font-family: Arial, Helvetica, sans-serif;
  background: #f6f7f8;
}

.editor-layout {
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
}

/* ─── Right Sidebar: Page Thumbnails ─────────────────────── */

.thumbnails-sidebar {
  width: 140px;
  min-width: 140px;
  height: 100%;
  overflow-y: auto;
  background: #ffffff;
  border-left: 1px solid #e2e8f0;
  padding: 16px 10px;
  display: flex;
  flex-direction: column;
  gap: 0;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 2px; }
}

.thumbnails-title {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
  margin: 0 0 12px 0;
  text-align: center;
}

.thumbnails-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.thumbnail-item {
  position: relative;
  cursor: pointer;
}

.thumbnail-placeholder {
  width: 100%;
  aspect-ratio: 3 / 4;
  background: #f1f5f9;
  border-radius: 6px;
  border: 2px solid #e2e8f0;
  transition: border-color 0.2s;
  overflow: hidden;
  cursor: pointer;

  &.active {
    border-color: #137fec;
    box-shadow: 0 0 0 3px rgba(19, 127, 236, 0.1);
  }

  &.loading {
    background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
  }
}

.thumbnail-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.page-badge {
  position: absolute;
  top: 5px;
  right: 5px;
  background: #64748b;
  color: white;
  font-size: 9px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 3px;
  line-height: 1.4;

  &.active {
    background: #137fec;
  }
}

/* ─── Center: Document Area ──────────────────────────────── */

.document-area {
  flex: 1;
  height: 100%;
  overflow-y: auto;
  background: #f6f7f8;
  position: relative;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: #f1f5f9; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
}

.loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.document-scroll {
  padding: 32px 24px;
  display: flex;
  justify-content: center;
  min-height: 100%;
}

.all-imgs-container {
  width: 793px;
  min-width: 793px;
  max-width: 793px;
  height: auto;
  display: inline-block;
  user-select: none;
  box-shadow: 4px 6px 18px rgba(0, 0, 0, 0.18), -2px 0 10px rgba(0, 0, 0, 0.07);

  img {
    width: 100%;
    display: block;
  }

  .all-imgs-item {
    position: relative;
    margin-bottom: 0;
    border: 1px solid #e2e8f0;

    &:not(:first-child) { border-top: none; }
  }
}

/* ─── Left Sidebar: Fields Panel ────────────────────────── */

.fields-sidebar {
  width: 250px;
  min-width: 250px;
  height: 100%;
  border-right: 1px solid #e2e8f0;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sidebar-header {
  padding: 18px 16px 14px;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
  text-align: right;
  direction: rtl;
}

.sidebar-title {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 3px 0;
  text-align: right;
}

.sidebar-subtitle {
  font-size: 12px;
  color: #94a3b8;
  margin: 0;
  text-align: right;
}

/* ─── Scrollable Fields List ─────────────────────────────── */

.fields-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 12px 12px 8px;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 2px; }
}

.field-section {
  margin-bottom: 16px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 6px;
  padding: 6px 8px;
  cursor: pointer;
  border-radius: 6px;
  direction: rtl;
  user-select: none;
  transition: background-color 0.15s;

  &:hover { background: #f1f5f9; }
}

.section-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.section-title-text {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  flex: 1;
  text-align: right;
}

.section-chevron {
  font-size: 11px;
  color: #64748b;
  transition: transform 0.2s ease;
  flex-shrink: 0;

  &.collapsed { transform: rotate(-90deg); }
}

.field-cards {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.field-card {
  display: flex;
  align-items: center;
  padding: 9px 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  cursor: pointer;
  transition: all 0.15s ease;
  direction: rtl;
  gap: 12px;

  &:hover {
    background: #ffffff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  }

  &[data-assignee="1"]:hover { border-color: #5F9CDD; }
  &[data-assignee="2"]:hover { border-color: #646CF0; }
  &[data-assignee="99"]:hover { border-color: #72B5A6; }
  &[data-assignee="label"]:hover,
  &[data-assignee="textbox"]:hover,
  &[data-assignee="checkbox"]:hover { border-color: #2F357B; }

  &:active { transform: scale(0.98); }
}

.field-card-name {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  text-align: right;
  flex: 1;
}

.field-icon {
  font-size: 14px;
  flex-shrink: 0;
}

/* Dot colors */
.dot-assignee1 { background-color: #5F9CDD; }
.dot-assignee2 { background-color: #646CF0; }
.dot-representer { background-color: #72B5A6; }
.dot-general { background-color: #2F357B; }

/* Icon colors */
.icon-assignee1 { color: #5F9CDD; }
.icon-assignee2 { color: #646CF0; }
.icon-representer { color: #72B5A6; }
.icon-general { color: #2F357B; }

/* ─── Sidebar Footer ─────────────────────────────────────── */

.sidebar-footer {
  flex-shrink: 0;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

/* ─── Field Properties ───────────────────────────────────── */

.field-properties {
  border-bottom: 1px solid #e2e8f0;
}

.properties-section-header {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 10px 14px 8px;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  direction: rtl;
  cursor: pointer;
  user-select: none;

  &:hover { background: #f8fafc; }
}

.props-header-icon {
  font-size: 12px;
  color: #94a3b8;
}

.props-toggle-icon {
  font-size: 11px;
  color: #94a3b8;
  margin-right: auto;
}

.properties-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  direction: rtl;

  tr {
    border-bottom: 1px solid #f1f5f9;

    &:last-child { border-bottom: none; }
  }
}

.prop-label {
  width: 72px;
  padding: 7px 10px;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  white-space: nowrap;
  text-align: right;
  vertical-align: middle;
}

.prop-value {
  padding: 5px 8px;
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
  vertical-align: middle;
  text-align: right;
}

.prop-dropdown-cell {
  padding: 2px 4px;
}

.property-input {
  width: 70px;
  text-align: center;
  border: none;
  background: transparent;
  font-size: 12px;
  color: #374151;
  padding: 2px 4px;
  border-radius: 4px;

  &:focus {
    outline: 2px solid #137fec;
    background: white;
  }

  &:read-only {
    color: #6b7280;
    cursor: default;
  }
}

.textbox-text-input {
  width: 100% !important;
  color: #2F357B !important;
  background: #ffffff !important;
  border: 2px solid #e8eaf6 !important;
  border-radius: 4px;
  padding: 3px 6px;
  font-size: 12px;

  &::placeholder { color: rgba(47, 53, 123, 0.4) !important; }
  &:focus { border-color: #2F357B !important; outline: none; }
}

.property-checkbox {
  width: 15px !important;
  height: 15px !important;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  border: 2px solid #2F357B;
  border-radius: 3px;
  background-color: white;
  position: relative;

  &:checked {
    background-color: #2F357B;
    &::after {
      content: "✕";
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: white;
      font-size: 10px;
      font-weight: bold;
      line-height: 1;
    }
  }
}

.no-options-message {
  padding: 6px 10px;
  text-align: center;
  color: #94a3b8;
  font-style: italic;
  font-size: 11px;
}

/* ─── Binding Dropdown ───────────────────────────────────── */

.buefy-dropdown {
  width: 100% !important;
  min-width: 100%;
  position: relative;
  z-index: 999;
  overflow: visible;
  display: block;

  .dropdown-trigger {
    width: 100% !important;
    padding: 4px 10px;
    font-size: 12px;
    font-family: Arial, Helvetica, sans-serif;
    border-radius: 0;
    direction: rtl;
    text-align: right;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: white !important;
    border-left: 2px solid #e8eaf6 !important;
    border-right: 2px solid #e8eaf6 !important;
    color: #2F357B !important;
    box-sizing: border-box;
    transition: background-color 0.15s;

    &:hover { background-color: #f5f5f5 !important; }

    .dropdown-arrow {
      font-size: 10px;
      color: #94a3b8;
    }
  }

  .search-container {
    padding: 6px 8px;
    background-color: #ffffff;
    border-left: 2px solid #e8eaf6;
    border-right: 2px solid #e8eaf6;

    .search-input {
      width: 100%;
      margin-bottom: 0;

      ::v-deep .control { width: 100%; }
      ::v-deep .input {
        width: 100%;
        font-family: Arial, Helvetica, sans-serif;
        border-color: #e2e8f0;
        border-style: solid;
        padding: 4px 10px;
        border-radius: 5px;
        font-size: 12px;
        color: #2F357B;
        &::placeholder { color: rgba(47, 53, 123, 0.5); }
      }
    }
  }

  .dropdown-content {
    max-height: 190px;
    overflow-y: auto;
    background-color: white;
    border: 2px solid #e8eaf6;
    border-top: none;
    border-radius: 0 0 4px 4px;
    direction: ltr;

    &::-webkit-scrollbar { width: 5px; }
    &::-webkit-scrollbar-track { background: rgba(47, 53, 123, 0.05); }
    &::-webkit-scrollbar-thumb { background: rgba(47, 53, 123, 0.2); border-radius: 3px; }
    scrollbar-width: thin;
    scrollbar-color: rgba(47, 53, 123, 0.2) transparent;
  }

  .group-header {
    background-color: #ede9fe;
    color: #4c1d95;
    font-weight: bold;
    padding: 6px 10px;
    border-bottom: 1px solid #ddd6fe;
    text-align: right;
    direction: rtl;

    strong { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
  }

  .group-option {
    padding: 7px 10px;
    cursor: pointer;
    background-color: white;
    color: #333;
    font-family: Arial, Helvetica, sans-serif;
    text-align: right;
    direction: rtl;
    font-size: 12px;
    font-weight: 600;
    transition: background-color 0.15s;

    &:hover { background-color: #f5f3ff; color: #2F357B; }
  }

  .group-separator {
    height: 1px;
    background: #f1f5f9;
    margin: 2px 0;
  }

  .loading-indicator {
    padding: 6px 10px;
    text-align: center;
    color: #94a3b8;
    font-size: 11px;
    font-style: italic;
  }
}

/* ─── Footer Actions ─────────────────────────────────────── */

.footer-actions {
  padding: 12px;
}

.save-btn {
  width: 100%;
  height: 42px;
  background: #137fec;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background-color 0.2s ease, opacity 0.2s ease;
  font-family: Arial, Helvetica, sans-serif;
  box-shadow: 0 2px 8px rgba(19, 127, 236, 0.25);

  &:hover:not(.disabled) { background: #0f6fd4; }
  &:active:not(.disabled) { background: #0d60bb; }

  &.disabled {
    opacity: 0.45;
    cursor: not-drop;
  }
}

/* ─── Draggable Field Overlays ───────────────────────────── */

.vdr-container {
  border: none;
  margin: 0 auto;
  font-size: 20px;
  font-family: sans-serif;
  padding: 20px;
  touch-action: none;
  direction: rtl;
  text-align: center;
  width: 200px;
  opacity: 0.9;
  box-sizing: border-box;
  position: absolute;
  z-index: 3;

  &.active {
    outline: none;
  }
}

/* Circular resize handles */
.vdr-handle {
  border-radius: 50% !important;
  background-color: white !important;
  border: 2px solid #94a3b8 !important;
  box-shadow: 0 1px 4px rgba(0,0,0,0.15) !important;
}

.draggable-resizable-field {
  position: relative;
  width: 100%;
  height: 100%;
  font-size: 12px;
  font-weight: 600;

  span { margin: 0; }

  .remove-button {
    position: absolute;
    top: 0;
    left: 0;
    transform: translate(-30px, -30px);
    font-family: cursive;
    font-style: normal;
    font-size: 12px;
    border-radius: 50%;
    padding: 2px 6px;
    line-height: 15px;
    cursor: pointer;
    z-index: 1000;
    color: white;
    border: none;
    opacity: 0;
    transition: opacity 0.15s;
  }

  &:hover .remove-button { opacity: 1; }
}

.text-field {
  span {
    position: absolute;
    top: 50%;
    right: 5px;
    transform: translateY(-50%);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: calc(100% - 10px);
  }
  .remove-button { transform: translate(-12px, -16px); }
}

.signature-field {
  span {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}

.checkbox-field {
  width: 28px !important;
  height: 28px !important;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 15px;
    height: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }

  .remove-button { transform: translate(-10px, -12px); }
}

.field-checkbox {
  width: 15px !important;
  height: 15px !important;
  cursor: default;
  appearance: none;
  -webkit-appearance: none;
  border: 2px solid currentColor;
  border-radius: 3px;
  background-color: transparent;
  position: relative;
  margin: 0;
  padding: 0;
  pointer-events: none;

  &:checked::after {
    content: "✕";
    position: absolute;
    top: 45%;
    left: 55%;
    transform: translate(-50%, -50%);
    color: inherit;
    font-size: 14px;
    font-weight: bold;
    line-height: 1;
  }

  &:disabled { opacity: 1; cursor: default; }
}

.text-field-container.vdr-container {
  padding: 2px !important;
  min-width: 80px !important;
  max-width: 350px !important;
}

.checkbox-field-container.vdr-container {
  padding: 0px !important;
  min-width: 28px !important;
  max-width: 28px !important;
  min-height: 28px !important;
  max-height: 28px !important;
  box-sizing: border-box !important;
  overflow: visible !important;
  z-index: 999 !important;
}

.text-field-container .vdr-handle.vdr-handle-t,
.text-field-container .vdr-handle.vdr-handle-b {
  display: none !important;
}

.checkbox-field-container .vdr-handle {
  display: none !important;
}

.past-attribute {
  pointer-events: none;
}

/* ─── Draggable Field Colors ─────────────────────────────── */

.ochre {
  background-color: rgba(95, 156, 221, 0.12) !important;
  border: 2px dashed #5F9CDD !important;
  border-radius: 6px !important;
  transition: background-color 0.2s ease;
  color: #5F9CDD;
  .draggable-resizable-field { color: #5F9CDD; }
  .remove-button { background-color: #5F9CDD; }
  .vdr-handle { border-color: #5F9CDD !important; }
  &:hover { background-color: rgba(95, 156, 221, 0.22) !important; }
  &.active { background-color: rgba(95, 156, 221, 0.22) !important; }
}

.light-gray {
  background-color: rgba(100, 108, 240, 0.12) !important;
  border: 2px dashed #646CF0 !important;
  border-radius: 6px !important;
  transition: background-color 0.2s ease;
  color: #646CF0;
  .draggable-resizable-field { color: #646CF0; }
  .remove-button { background-color: #646CF0; }
  .vdr-handle { border-color: #646CF0 !important; }
  &:hover { background-color: rgba(100, 108, 240, 0.22) !important; }
  &.active { background-color: rgba(100, 108, 240, 0.22) !important; }
}

.light-blue {
  background-color: rgba(114, 181, 166, 0.12) !important;
  border: 2px dashed #72B5A6 !important;
  border-radius: 6px !important;
  transition: background-color 0.2s ease;
  color: #72B5A6;
  .draggable-resizable-field { color: #72B5A6; }
  .remove-button { background-color: #72B5A6; }
  .vdr-handle { border-color: #72B5A6 !important; }
  &:hover { background-color: rgba(114, 181, 166, 0.22) !important; }
  &.active { background-color: rgba(114, 181, 166, 0.22) !important; }
}

.teal {
  background-color: rgba(47, 53, 123, 0.12) !important;
  border: 2px dashed #2F357B !important;
  border-radius: 6px !important;
  transition: background-color 0.2s ease;
  color: #2F357B;
  .draggable-resizable-field { color: #2F357B; }
  .remove-button { background-color: #2F357B; }
  .vdr-handle { border-color: #2F357B !important; }
  &:hover { background-color: rgba(47, 53, 123, 0.22) !important; }
  &.active { background-color: rgba(47, 53, 123, 0.22) !important; }
}

.text-field-container.teal {
  background-color: rgba(47, 53, 123, 0.12) !important;
  &:hover { background-color: rgba(47, 53, 123, 0.22) !important; }
  &.active { background-color: rgba(47, 53, 123, 0.22) !important; }
}

/* ─── Loading Spinner ────────────────────────────────────── */

.spinner {
  width: 40px;
  height: 40px;
  position: relative;
}

.double-bounce1,
.double-bounce2 {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: #137fec;
  opacity: 0.6;
  position: absolute;
  top: 0;
  left: 0;
  animation: bounce 2s infinite ease-in-out;
}

.double-bounce2 {
  animation-delay: -1s;
}

@keyframes bounce {
  0%, 100% { transform: scale(0); }
  50% { transform: scale(1); }
}


</style>

<!-- Toast styles must be unscoped because toasts are teleported to <body> -->
<style lang="scss">
.toast-pill {
  position: fixed;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(15, 23, 42, 0.92);
  color: white;
  padding: 12px 20px;
  border-radius: 9999px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(8px);
  max-width: 480px;
  direction: rtl;
}

.toast-icon {
  font-size: 16px;
  color: #137fec;
  flex-shrink: 0;
}

.toast-icon-success {
  color: #34d399;
}

.toast-icon-error {
  color: #f87171;
}

.toast-message {
  font-size: 13px;
  font-weight: 600;
  margin: 0;
  white-space: nowrap;
}

.toast-close {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  font-size: 13px;
  padding: 0 0 0 6px;
  line-height: 1;
  transition: color 0.15s;
  flex-shrink: 0;

  &:hover { color: white; }
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(12px);
}
</style>
